import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorIcon from "@mui/icons-material/ErrorOutlined";
import FlexBetween from "../../components/FlexBetween";
import Loader from "../../components/Molecules/Loader/Loader";
import CommentBeforeUpdateDialog from "../../components/Dialogs/CommentBeforeUpdateDialog";
import AutoCompleteRuleList from "../../components/Molecules/Rule/AutoCompleteRuleList";
import AutoCompleteDomainList from "../../components/Molecules/Domain/AutoCompleteDomainList";
import { useToast, extractApiErrorMessage } from "../../services/utils";
import {
  createComparisonScoringConfig,
  getComparisonScoringConfigById,
  updateComparisonScoringConfig,
} from "../../services/scoringService";
import { getRuleDetail } from "../../services/rulesService";
import useFetchComparisonScoringConfigById from "../../hooks/useFetchComparisonScoringConfigById";
import {
  comparisonScoringConfigDefaults,
  scoringSeverityOptions,
  customFactorTargetOptions,
} from "../../services/constants";
import { comparisonScoringConfigFormSchema } from "../../schemas";
import { isScoringSubmitDisabled } from "../../utils/scoringConfigFormValidation";
import {
  ICreateComparisonScoringConfig,
  IEditComparisonScoringConfig,
} from "../../types/Scoring.type";
import { useBreadCrumbContext } from "../../contexts/BreadCrumbContext";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import { validateConfigRequest } from "../../services/resourcesService";

const AddEditComparisonScoring: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { id: configId } = useParams();
  const { setCurrentBreadcrumbPage } = useBreadCrumbContext();
  const isEditMode = Boolean(configId);

  const [isLoading, setIsLoading] = useState(false);
  const [openComment, setOpenComment] = useState(false);
  const [comment, setComment] = useState("");
  const [currentDomainId, setCurrentDomainId] = useState<number | null>(null);
  const [domainError, setDomainError] = useState<string>("");
  const [ruleId, setRuleId] = useState<number | null>(null);
  const [ruleName, setRuleName] = useState<string>("");
  const [ruleError, setRuleError] = useState<string>("");
  const [severityMappings, setSeverityMappings] = useState<
    Record<string, string>
  >({});
  const [initialSeverityMappings, setInitialSeverityMappings] = useState<
    Record<string, string>
  >({});
  const [checkNameOptions, setCheckNameOptions] = useState<string[]>([]);
  const newMappingCounter = useRef(0);

  const [configData] = useFetchComparisonScoringConfigById({
    setIsLoading,
    configId: configId ? parseInt(configId) : undefined,
  });
  const [localConfigOverride, setLocalConfigOverride] = useState<any>(null);
  const effectiveConfig = localConfigOverride || configData;
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    setCurrentBreadcrumbPage({
      name: isEditMode ? "Edit Comparison Scoring" : "Add Comparison Scoring",
      id: configId ? parseInt(configId) : null,
    });
    return () => setCurrentBreadcrumbPage({ name: "", id: null });
  }, [configId]);

  // Populate state from loaded config (edit mode)
  useEffect(() => {
    if (effectiveConfig?.rule_id) {
      setRuleId(effectiveConfig.rule_id);
      const mappings = effectiveConfig.severity_mappings || {};
      setSeverityMappings(mappings);
      setInitialSeverityMappings(JSON.parse(JSON.stringify(mappings)));
    }
  }, [effectiveConfig]);

  // Fetch check name options when we have a rule id
  useEffect(() => {
    const fetchCheckNames = async () => {
      try {
        const ruleData = await getRuleDetail(ruleId as number);
        if (ruleData?.name) setRuleName(ruleData.name);
        const domainNames = (
          ruleData?.rule_schema?.domain_comparison_rules || []
        ).map((r: any) => r.name);
        const customNames = (
          ruleData?.rule_schema?.custom_comparison_rules || []
        ).map((r: any) => r.name);
        setCheckNameOptions([...domainNames, ...customNames]);
      } catch (err) {
        console.error("Failed to load check names:", err);
      }
    };
    if (ruleId && ruleId > 0) fetchCheckNames();
  }, [ruleId]);

  const formik = useFormik({
    initialValues: {
      blend_ratio:
        effectiveConfig?.blend_ratio ??
        comparisonScoringConfigDefaults.blend_ratio,
      category_weight_missing:
        effectiveConfig?.category_weight_missing ??
        comparisonScoringConfigDefaults.category_weight_missing,
      category_weight_mismatch:
        effectiveConfig?.category_weight_mismatch ??
        comparisonScoringConfigDefaults.category_weight_mismatch,
      category_weight_validation:
        effectiveConfig?.category_weight_validation ??
        comparisonScoringConfigDefaults.category_weight_validation,
      missing_factor:
        effectiveConfig?.missing_factor ??
        comparisonScoringConfigDefaults.missing_factor,
      mismatch_factor_low:
        effectiveConfig?.mismatch_factor_low ??
        comparisonScoringConfigDefaults.mismatch_factor_low,
      mismatch_factor_medium:
        effectiveConfig?.mismatch_factor_medium ??
        comparisonScoringConfigDefaults.mismatch_factor_medium,
      mismatch_factor_high:
        effectiveConfig?.mismatch_factor_high ??
        comparisonScoringConfigDefaults.mismatch_factor_high,
      custom_factor_sql: effectiveConfig?.custom_factor_sql || "",
      custom_factor_target: effectiveConfig?.custom_factor_target || "",
      comment: "",
    },
    validationSchema: comparisonScoringConfigFormSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const cleanMappings = Object.fromEntries(
        Object.entries(severityMappings).filter(
          ([k]) => k !== "" && !k.startsWith("__new_"),
        ),
      );

      if (!isEditMode) {
        let hasError = false;
        if (!currentDomainId) {
          setDomainError("Domain is required");
          hasError = true;
        }
        if (!ruleId) {
          setRuleError("Rule is required");
          hasError = true;
        }
        if (hasError) return;
        setIsLoading(true);
        try {
          const createBody: ICreateComparisonScoringConfig = {
            rule_id: ruleId as number,
            blend_ratio: values.blend_ratio,
            category_weight_missing: values.category_weight_missing,
            category_weight_mismatch: values.category_weight_mismatch,
            category_weight_validation: values.category_weight_validation,
            missing_factor: values.missing_factor,
            mismatch_factor_low: values.mismatch_factor_low,
            mismatch_factor_medium: values.mismatch_factor_medium,
            mismatch_factor_high: values.mismatch_factor_high,
            custom_factor_sql: values.custom_factor_sql || null,
            custom_factor_target: values.custom_factor_target || null,
            severity_mappings: cleanMappings,
          };
          await createComparisonScoringConfig(createBody);
          showToast("Comparison scoring config created!", "success");
          navigate("/scoring-config?tab=0&runSub=1");
        } catch (error) {
          showToast(
            extractApiErrorMessage(error, "Failed to create config"),
            "error",
          );
        } finally {
          setIsLoading(false);
        }
      } else {
        setOpenComment(true);
      }
    },
  });

  // Weight sum validation
  const weightsSum =
    Number(formik.values.category_weight_missing || 0) +
    Number(formik.values.category_weight_mismatch || 0) +
    Number(formik.values.category_weight_validation || 0);
  const weightsSumValid = Math.abs(weightsSum - 1.0) < 0.001;

  // Track changes (edit mode)
  const mappingsChanged =
    JSON.stringify(initialSeverityMappings) !==
    JSON.stringify(
      Object.fromEntries(
        Object.entries(severityMappings).filter(
          ([k]) => k !== "" && !k.startsWith("__new_"),
        ),
      ),
    );
  const hasChanges = formik.dirty || mappingsChanged;

  const buildPayload = () => {
    const cleanMappings = Object.fromEntries(
      Object.entries(severityMappings).filter(
        ([k]) => k !== "" && !k.startsWith("__new_"),
      ),
    );
    if (isEditMode) {
      // Mirrors the editBody sent by handleSaveComment to updateComparisonScoringConfig.
      // Uses the live `comment` state so the View JSON always reflects the
      // exact payload that would be sent if Save were clicked right now.
      // Include rule_id so the payload can be used by Config application.
      return {
        rule_id: ruleId,
        category_weight_missing: formik.values.category_weight_missing,
        category_weight_mismatch: formik.values.category_weight_mismatch,
        category_weight_validation: formik.values.category_weight_validation,
        missing_factor: formik.values.missing_factor,
        mismatch_factor_low: formik.values.mismatch_factor_low,
        mismatch_factor_medium: formik.values.mismatch_factor_medium,
        mismatch_factor_high: formik.values.mismatch_factor_high,
        custom_factor_sql: formik.values.custom_factor_sql || null,
        custom_factor_target: formik.values.custom_factor_target || null,
        severity_mappings: cleanMappings,
        comment,
      };
    }
    return {
      rule_id: ruleId,
      blend_ratio: formik.values.blend_ratio,
      category_weight_missing: formik.values.category_weight_missing,
      category_weight_mismatch: formik.values.category_weight_mismatch,
      category_weight_validation: formik.values.category_weight_validation,
      missing_factor: formik.values.missing_factor,
      mismatch_factor_low: formik.values.mismatch_factor_low,
      mismatch_factor_medium: formik.values.mismatch_factor_medium,
      mismatch_factor_high: formik.values.mismatch_factor_high,
      custom_factor_sql: formik.values.custom_factor_sql || null,
      custom_factor_target: formik.values.custom_factor_target || null,
      severity_mappings: cleanMappings,
    };
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: isEditMode
          ? `/comparison-scoring-config/${configId}`
          : "/comparison-scoring-config",
        method: isEditMode ? "PUT" : "POST",
        body: payload,
        query_params: {},
        url_params: isEditMode ? { id: configId } : {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) showToast("Payload is valid!", "success");
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    await navigator.clipboard.writeText(
      JSON.stringify(buildPayload(), null, 2),
    );
    showToast("Payload copied to clipboard!", "success");
  };

  const handleResetToDefaults = () => {
    formik.setValues({
      blend_ratio: comparisonScoringConfigDefaults.blend_ratio,
      category_weight_missing:
        comparisonScoringConfigDefaults.category_weight_missing,
      category_weight_mismatch:
        comparisonScoringConfigDefaults.category_weight_mismatch,
      category_weight_validation:
        comparisonScoringConfigDefaults.category_weight_validation,
      missing_factor: comparisonScoringConfigDefaults.missing_factor,
      mismatch_factor_low: comparisonScoringConfigDefaults.mismatch_factor_low,
      mismatch_factor_medium:
        comparisonScoringConfigDefaults.mismatch_factor_medium,
      mismatch_factor_high:
        comparisonScoringConfigDefaults.mismatch_factor_high,
      custom_factor_sql: "",
      custom_factor_target: "",
      comment: formik.values.comment,
    });
    setSeverityMappings({});
  };

  // Severity mapping handlers
  const entries = Object.entries(severityMappings);

  const handleAddMapping = () => {
    const tempKey = `__new_${newMappingCounter.current++}`;
    setSeverityMappings((prev) => ({ ...prev, [tempKey]: "medium" }));
  };

  const handleRemoveMapping = (checkName: string) => {
    setSeverityMappings((prev) => {
      const next = { ...prev };
      delete next[checkName];
      return next;
    });
  };

  const handleUpdateCheckName = (oldName: string, newName: string) => {
    if (newName === oldName) return;
    setSeverityMappings((prev) => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([k, v]) => {
        next[k === oldName ? newName : k] = v;
      });
      return next;
    });
  };

  const handleUpdateSeverity = (checkName: string, severity: string) => {
    setSeverityMappings((prev) => ({ ...prev, [checkName]: severity }));
  };

  const handleSaveClick = () => {
    const hasEmptyOrTempKey = Object.keys(severityMappings).some(
      (k) => k === "" || k.startsWith("__new_"),
    );
    if (hasEmptyOrTempKey) {
      showToast(
        "Please select a check name for all severity mappings before saving.",
        "error",
      );
      return;
    }
    formik.handleSubmit();
  };

  const handleSaveComment = async () => {
    setOpenComment(false);
    setIsLoading(true);
    try {
      const cleanMappings = Object.fromEntries(
        Object.entries(severityMappings).filter(
          ([k]) => k !== "" && !k.startsWith("__new_"),
        ),
      );
      const editBody: IEditComparisonScoringConfig = {
        blend_ratio: formik.values.blend_ratio,
        category_weight_missing: formik.values.category_weight_missing,
        category_weight_mismatch: formik.values.category_weight_mismatch,
        category_weight_validation: formik.values.category_weight_validation,
        missing_factor: formik.values.missing_factor,
        mismatch_factor_low: formik.values.mismatch_factor_low,
        mismatch_factor_medium: formik.values.mismatch_factor_medium,
        mismatch_factor_high: formik.values.mismatch_factor_high,
        custom_factor_sql: formik.values.custom_factor_sql || null,
        custom_factor_target: formik.values.custom_factor_target || null,
        severity_mappings: cleanMappings,
        comment,
      };
      await updateComparisonScoringConfig(parseInt(configId!), editBody);
      showToast("Config updated!", "success");
      // Refetch from server so enableReinitialize picks up fresh data
      const refreshed = await getComparisonScoringConfigById(
        parseInt(configId!),
      );
      if (refreshed) {
        setLocalConfigOverride(refreshed);
        const newMappings = refreshed.severity_mappings || {};
        setSeverityMappings(newMappings);
        setInitialSeverityMappings(JSON.parse(JSON.stringify(newMappings)));
      }
    } catch (error) {
      showToast(
        extractApiErrorMessage(error, "Failed to update config"),
        "error",
      );
    } finally {
      setIsLoading(false);
      setComment("");
    }
  };

  const handleChangeRule = (_e: any, value: any) => {
    setRuleId(value?.id ?? null);
    setRuleName(value?.name ?? "");
    if (ruleError) setRuleError("");
    setSeverityMappings({});
  };

  const handleChangeDomain = (_e: any, value: any) => {
    setCurrentDomainId(value?.id ?? null);
    setRuleId(null);
    setRuleName("");
    setSeverityMappings({});
    if (domainError) setDomainError("");
    if (ruleError) setRuleError("");
  };

  const weightField = (
    label: string,
    name:
      | "category_weight_missing"
      | "category_weight_mismatch"
      | "category_weight_validation",
  ) => (
    <Grid item xs={12} md={3}>
      <label className="label-text">
        {label}
        <span className="required-asterisk">*</span>
      </label>
      <TextField
        name={name}
        type="number"
        fullWidth
        inputProps={{ step: "any", min: 0, max: 1 }}
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        onWheel={(e) => (e.target as HTMLInputElement).blur()}
        className={`form-control ${
          formik.touched[name] && formik.errors[name] ? "has-error" : ""
        }`}
        error={formik.touched[name] && Boolean(formik.errors[name])}
        helperText={
          (formik.touched[name] && (formik.errors[name] as string)) || " "
        }
      />
    </Grid>
  );

  const mismatchField = (
    label: string,
    name:
      | "mismatch_factor_low"
      | "mismatch_factor_medium"
      | "mismatch_factor_high",
  ) => (
    <Grid item xs={12} md={4}>
      <label className="label-text">
        {label}
        <span className="required-asterisk">*</span>
      </label>
      <TextField
        name={name}
        type="number"
        fullWidth
        inputProps={{ step: "any", min: 0, max: 1 }}
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        onWheel={(e) => (e.target as HTMLInputElement).blur()}
        className={`form-control ${formik.errors[name] ? "has-error" : ""}`}
        error={Boolean(formik.errors[name])}
        helperText={(formik.errors[name] as string) || " "}
      />
    </Grid>
  );

  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <Box>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSaveClick();
          }}
          autoComplete="off"
        >
          <Box className="text-box-card-white m-0">
            <Box className="text-box-header">
              <h3>{isEditMode ? "Edit" : "Add"} Comparison Scoring Config</h3>
            </Box>

            {/* Domain & Rule selection */}
            <Grid
              container
              rowSpacing={2.5}
              columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
            >
              {!isEditMode && (
                <Grid item xs={12} sm={6} md={4}>
                  <AutoCompleteDomainList
                    setIsLoading={setIsLoading}
                    handelOnChangeDomain={handleChangeDomain}
                    currentDomainId={currentDomainId}
                    className={`form-control-autocomplete ${
                      domainError ? "has-error" : ""
                    }`}
                    required
                    error={Boolean(domainError)}
                    helperText={domainError}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={4}>
                {isEditMode ? (
                  <>
                    <label className="label-text">Rule</label>
                    <TextField
                      fullWidth
                      value={ruleName}
                      InputProps={{ readOnly: true }}
                      className="form-control"
                    />
                  </>
                ) : (
                  <AutoCompleteRuleList
                    setIsLoading={setIsLoading}
                    handelOnChangeRule={handleChangeRule}
                    currentRuleId={ruleId}
                    currentDomainId={currentDomainId}
                    className={`form-control-autocomplete ${
                      ruleError ? "has-error" : ""
                    }`}
                    required
                    error={Boolean(ruleError)}
                    helperText={ruleError}
                    isDisabled={!currentDomainId}
                    filterByScoreConfigAssociated
                  />
                )}
              </Grid>
            </Grid>

            {/* Category Weights Section */}
            <Typography
              variant="body2"
              sx={{ mt: "20px", mb: 1, fontWeight: 600 }}
            >
              Category Weights
            </Typography>
            <Grid
              container
              rowSpacing={2.5}
              columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
            >
              {weightField("Missing Weight", "category_weight_missing")}
              {weightField("Mismatched Weight", "category_weight_mismatch")}
              {weightField("Validation Weight", "category_weight_validation")}
              <Grid
                item
                xs={12}
                md={3}
                sx={{ display: "flex", alignItems: "center" }}
              >
                {weightsSumValid ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color: "success.main",
                    }}
                  >
                    <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="body2">
                      Sum: {weightsSum.toFixed(2)}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color: "error.main",
                    }}
                  >
                    <ErrorIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="body2">
                      Sum: {weightsSum.toFixed(2)} (must be 1.0)
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>

            {/* Missing Records Section */}
            <Typography
              variant="body2"
              sx={{ mt: "20px", mb: 1, fontWeight: 600 }}
            >
              Missing Records
            </Typography>
            <Grid
              container
              rowSpacing={2.5}
              columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
            >
              <Grid item xs={12} md={4}>
                <label className="label-text">
                  Blend Ratio
                  <span className="required-asterisk">*</span>
                </label>
                <TextField
                  name="blend_ratio"
                  type="number"
                  fullWidth
                  inputProps={{ step: "any", min: 0, max: 1 }}
                  value={formik.values.blend_ratio}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  className={`form-control ${
                    formik.touched.blend_ratio && formik.errors.blend_ratio
                      ? "has-error"
                      : ""
                  }`}
                  error={
                    formik.touched.blend_ratio &&
                    Boolean(formik.errors.blend_ratio)
                  }
                  helperText={
                    (formik.touched.blend_ratio &&
                      (formik.errors.blend_ratio as string)) ||
                    " "
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <label className="label-text">
                  Missing Factor
                  <span className="required-asterisk">*</span>
                </label>
                <TextField
                  name="missing_factor"
                  type="number"
                  fullWidth
                  inputProps={{ step: "any", min: 0 }}
                  value={formik.values.missing_factor}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  className={`form-control ${
                    formik.touched.missing_factor &&
                    formik.errors.missing_factor
                      ? "has-error"
                      : ""
                  }`}
                  error={
                    formik.touched.missing_factor &&
                    Boolean(formik.errors.missing_factor)
                  }
                  helperText={
                    (formik.touched.missing_factor &&
                      (formik.errors.missing_factor as string)) ||
                    " "
                  }
                />
              </Grid>
            </Grid>

            {/* Mismatched Records Section */}
            <Typography
              variant="body2"
              sx={{ mt: "20px", mb: 1, fontWeight: 600 }}
            >
              Mismatched Records
            </Typography>
            <Grid
              container
              rowSpacing={2.5}
              columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
            >
              {mismatchField("Factor (Low)", "mismatch_factor_low")}
              {mismatchField("Factor (Medium)", "mismatch_factor_medium")}
              {mismatchField("Factor (High)", "mismatch_factor_high")}
            </Grid>

            {/* Severity Mappings Section */}
            <Typography
              variant="body2"
              sx={{ mt: "20px", mb: 1, fontWeight: 600 }}
            >
              Severity Mappings (check_name to severity)
            </Typography>
            <Box
              sx={{
                border: "1px solid var(--table-border)",
                borderRadius: "4px",
                mb: 2,
                overflow: "hidden",
              }}
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Check Name</strong>
                      </TableCell>
                      <TableCell sx={{ width: 160 }}>
                        <strong>Severity</strong>
                      </TableCell>
                      <TableCell align="center" sx={{ width: 60 }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map(([checkName, severity]) => {
                      const usedNames = Object.keys(severityMappings).filter(
                        (n) => n !== checkName,
                      );
                      const availableOptions = checkNameOptions.filter(
                        (opt) => !usedNames.includes(opt),
                      );
                      return (
                        <TableRow key={checkName}>
                          <TableCell>
                            <Autocomplete
                              value={
                                checkName.startsWith("__new_")
                                  ? null
                                  : checkName || null
                              }
                              options={availableOptions}
                              onChange={(_e, newValue) =>
                                handleUpdateCheckName(checkName, newValue || "")
                              }
                              className="form-control-autocomplete dropdown-padding-1"
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  className="form-control"
                                  placeholder="Select check name"
                                />
                              )}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ width: 160 }}>
                            <Select
                              fullWidth
                              value={severity}
                              onChange={(e) =>
                                handleUpdateSeverity(checkName, e.target.value)
                              }
                              size="small"
                            >
                              {scoringSeverityOptions.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                  {opt}
                                </MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveMapping(checkName)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Box sx={{ p: 1, borderTop: "1px solid var(--table-border)" }}>
                  <Button
                    size="small"
                    className="btn-orange btn-sm"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddMapping}
                  >
                    Add Mapping
                  </Button>
                </Box>
              </TableContainer>
            </Box>

            {/* Custom SQL Override (Collapsible) */}
            <Accordion
              sx={{
                mb: 2,
                border: "1px solid var(--table-border)",
                boxShadow: "none",
                "&:before": { display: "none" },
                borderRadius: "4px !important",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Custom Factor SQL Override (Advanced)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid
                  container
                  rowSpacing={2.5}
                  columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
                >
                  <Grid item xs={12} md={4}>
                    <label className="label-text">Apply Factor To</label>
                    <Select
                      fullWidth
                      name="custom_factor_target"
                      value={formik.values.custom_factor_target}
                      onChange={formik.handleChange}
                      size="small"
                      displayEmpty
                      className="form-control-autocomplete"
                    >
                      <MenuItem value="">
                        <em>None (disabled)</em>
                      </MenuItem>
                      {customFactorTargetOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12}>
                    <label className="label-text">
                      Custom Factor SQL{" "}
                      <span style={{ fontWeight: 400, opacity: 0.7 }}>
                        (must return a single float value)
                      </span>
                    </label>
                    <TextField
                      name="custom_factor_sql"
                      fullWidth
                      value={formik.values.custom_factor_sql}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="form-control"
                      multiline
                      rows={4}
                      placeholder="SELECT CAST(custom_value AS FLOAT) FROM custom_factors WHERE ..."
                      disabled={!formik.values.custom_factor_target}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>

          <FlexBetween gap={2.5} sx={{ mt: 1, justifyContent: "flex-end" }}>
            <Button
              type="button"
              className="btn-orange btn-dark"
              variant="contained"
              disabled={formik.isSubmitting || isLoading}
              onClick={handleResetToDefaults}
              startIcon={<RestoreIcon />}
            >
              Reset to Defaults
            </Button>
            <ViewJsonSplitButton
              onViewJson={() => setIsPayloadDialogOpen(true)}
              onCopyJson={handleCopyPayload}
            />
            <Button
              type="submit"
              className="btn-orange"
              color="secondary"
              variant="contained"
              disabled={isScoringSubmitDisabled({
                isSubmitting: formik.isSubmitting,
                isLoading,
                isValid: formik.isValid,
                isEditMode,
                hasChanges,
                hasAdditionalInvalidState: !weightsSumValid,
              })}
              startIcon={
                formik.isSubmitting || isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveOutlinedIcon />
                )
              }
            >
              {formik.isSubmitting || isLoading ? "Saving..." : "Save"}
            </Button>
          </FlexBetween>
        </form>
      </Box>
      <CommentBeforeUpdateDialog
        title="Confirm updating Comparison Scoring Config"
        dialogContent={
          <div>
            <p className="m-0 mb-2">Add a note before updating</p>
            <textarea
              value={comment}
              className="form-control-1 max-60"
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        }
        openConfirmation={openComment}
        handleSaveComment={handleSaveComment}
        handleCancel={() => {
          setOpenComment(false);
          setComment("");
        }}
      />
      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        dialogTitle="Comparison Scoring Config Payload"
        payload={buildPayload()}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </>
  );
};

export default AddEditComparisonScoring;
